import React from 'react';
import { iSmartphone, iTrendingUp } from '@hover/icons';
import { StaticImage } from 'gatsby-plugin-image';
import { Body, Box, Link } from '@hover/blueprint';
import type { IMeasurePros } from '../interfaces/IHoverPros';
import type { SolutionTestimonyItem } from '../interfaces/ISolutionTestimony';
import type { ISolutionCard } from '../interfaces/ISolutionCard';
import type { ISolutionPoints } from '../interfaces/ISolutionPoints';
import type { ISolutionPartnerTableProps } from '../interfaces/ISolutionsPartners';
import type { ILearningCardProps } from '../components/common/learning-card';
import MagnifyingGlass from '../assets/svgs/MagnifyingGlass';

export const MEASURE_PROS: IMeasurePros = [
  {
    description: (
      <>
        Material-level detail
        <br />
        aerial can’t detect
      </>
    ),
    icon: <MagnifyingGlass />,
    key: 1,
    isVector: true,
  },
  {
    description: (
      <>
        Eliminates rework
        <br />
        and revisits
      </>
    ),
    icon: iTrendingUp,
    key: 2,
  },
  {
    description: (
      <>
        No special training
        <br />
        or equipment
      </>
    ),
    icon: iSmartphone,
    key: 3,
  },
];

export const MEASURE_TESTIMONIES: SolutionTestimonyItem[] = [
  {
    quote:
      'Without a HOVER report, I lose 2 hours for every measure… then 2 hours for remeasures in some cases. When you’re doing 70,000 squares roofing in a year, having HOVER is like a doctor having a scalpel.”',
    name: 'Max Bumgardner',
    role: 'SUTTON’S',
    image: (
      <StaticImage
        objectFit="contain"
        src="../assets/images/measure_testimony_3.png"
        alt=""
      />
    ),
    id: 1,
  },
  {
    quote:
      'Instead of spending an hour fumbling with my tape measure and risking a fall, all I had to do was take photos which allowed me to be more engaged with my homeowner and his needs”',
    name: 'CASEY PAPP',
    role: 'J&B West roofing & Construction - indiana',
    image: (
      <StaticImage
        objectFit="contain"
        src="../assets/images/measure_testimony_1.png"
        alt=""
      />
    ),
    id: 2,
  },
  {
    quote:
      'HOVER measurements make our life tremendously easier. By having confidence in their accuracy, I can tell my customers their price is not going to change.',
    name: 'THOMAS WILLIAMS',
    role: 'ALL AMERICAN RENOVATIONS - georgia',
    image: (
      <StaticImage
        objectFit="contain"
        src="../assets/images/measure_testimony_2.png"
        alt=""
      />
    ),
    id: 3,
  },
  {
    quote:
      "HOVER is one of the most accurate takeoff softwares that I've had the chance to work with. HOVER allows myself and my team to go spend five minutes on property, take photos, and get us all of our takeoffs.”",
    name: 'josh sierra, general manager',
    role: 'Sprague roofing and exteriors — COLORADO',
    image: (
      <StaticImage
        objectFit="contain"
        src="../assets/images/measure_testimony_4.png"
        alt=""
      />
    ),
    id: 4,
  },
];

export const MEASURE_CARDS: ISolutionCard[] = [
  {
    id: 1,
    description:
      'Detailed siding measurements and 3D model with just a few photos or a blueprint.',
    title: 'SIDING',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_cards_1.jpg"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_cards_1.jpg"
      />
    ),
  },
  {
    id: 2,
    description:
      'Complete roofing measurements and 3D model, all from ground level photos or a blueprint.',
    title: 'ROOFING',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_cards_2.jpg"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_cards_2.jpg"
      />
    ),
  },
  {
    id: 3,
    description:
      'Comprehensive measurements that include all of your project’s details.',
    title: 'GUTTERS, WINDOWS, & DOORS',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_cards_3.jpg"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_cards_3.jpg"
      />
    ),
  },
  {
    id: 4,
    description:
      'Detailed measurements and 2D plan from a guided photo capture, room by room. ',
    title: 'INTERIORS',
    learnMoreLink: '/interiors',
    isBeta: true,
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_cards_4.jpg"
        layout="constrained"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_cards_4.jpg"
        objectFit="contain"
      />
    ),
  },
];

export const COMPLETE_PROPERTY_DATA_POINTS: ISolutionPoints['items'] = [
  {
    id: 1,
    content: 'Get siding detail down to the trim, openings, frieze and more.',
  },
  {
    id: 2,
    content:
      'Get roofing details for facets, valleys, rakes, eaves, flashing, soffit, and more.',
  },
  {
    id: 3,
    content: 'Get interior dimensions for walls, doors, windows, and floors.',
  },
  {
    id: 4,
    content: 'Get material quantities instantly with material lists.',
  },
];

export const WITHOUT_BREAKING_SWEAT_POINTS: ISolutionPoints['items'] = [
  {
    id: 1,
    content:
      'Simply take smartphone photos of any property and HOVER does the rest.',
  },
  {
    id: 2,
    content: 'Quickly upload a blueprint for new builds or major remodels.',
  },
  {
    id: 3,
    content: 'Get a comprehensive PDF report you’re whole team can depend on.',
  },
  {
    id: 4,
    content: 'Automatically calculate siding and roofing materials needed.',
  },
];

export const POWER_TAKEOFFS_POINTS: ISolutionPoints['items'] = [
  {
    id: 1,
    content: 'Generate takeoffs with ease',
  },
  {
    id: 2,
    content: (
      <Body w="calc(100% - 40px)">
        Bring{' '}
        <Link
          href="/visualize"
          textDecor="underline"
          textDecorationColor="primary.base"
          color="primary.base"
        >
          design
        </Link>{' '}
        ideas to life
      </Body>
    ),
  },
  {
    id: 3,
    content: (
      <Body w="calc(100% - 40px)">
        Confidently generate{' '}
        <Link
          href="/estimate-and-close/"
          textDecor="underline"
          textDecorationColor="primary.base"
          color="primary.base"
        >
          estimates
        </Link>
      </Body>
    ),
  },
  {
    id: 4,
    content: 'Centralize data from inspections with photos and checklists.',
  },
];

export const FITS_INTO_YOUR_PROCESS_POINTS: ISolutionPoints['items'] = [
  {
    id: 1,
    content: 'Use in the office or in the field.',
  },
  {
    id: 2,
    content:
      'View measurements on an interactive 3D model for clarity and perspective. ',
  },
  {
    id: 3,
    content: (
      <Body w="calc(100% - 40px)">
        Integrates with software like Verisk, JobNimbus, JobProgress, CoreLogic,
        and more.{' '}
        <Link
          href="/estimate-and-close/"
          textDecor="underline"
          textDecorationColor="primary.base"
          color="primary.base"
        >
          Explore Integrations.
        </Link>
      </Body>
    ),
  },
  {
    id: 4,
    content: 'Export measurements to file types like PDF, XLS, XML, or 3D BIM.',
  },
];

export const MEASURE_PROFESSIONALS_PARTNERS: ISolutionPartnerTableProps['partners'] =
  [
    {
      key: 1,
      logo: (
        <Box width={{ base: '165px', tablet: '178px' }}>
          <StaticImage
            src="../assets/images/state-farm_logo_black.png"
            alt="StateFarm"
          />
        </Box>
      ),
    },
    {
      key: 2,
      logo: (
        <Box width={{ base: '165px', tablet: '189px' }}>
          <StaticImage
            src="../assets/images/travelers_vector_logo.png"
            alt="Travellers"
          />
        </Box>
      ),
    },
    {
      key: 3,
      logo: (
        <Box width={{ base: '154px', tablet: '157px' }}>
          <StaticImage
            src="../assets/images/dabella_logo_black.png"
            alt="DaBella"
          />
        </Box>
      ),
    },
    {
      key: 4,
      logo: (
        <Box width={{ base: '157px', tablet: '160px' }}>
          <StaticImage
            src="../assets/images/renewal-by-andersen_logo.png"
            alt="Renewal by Andersen"
          />
        </Box>
      ),
    },
    {
      key: 5,
      logo: (
        <Box width={{ base: '123px', tablet: '167px' }}>
          <StaticImage
            src="../assets/images/erie_home_logo_black.png"
            alt="ErieHome"
          />
        </Box>
      ),
    },
  ];

export const MEASURE_PROFESSIONALS_PARTNERS_MOBILE: ISolutionPartnerTableProps['partners'] =
  [
    {
      key: 1,
      logo: (
        <Box width={{ base: '140px', tablet: '178px' }}>
          <StaticImage
            src="../assets/images/state-farm_logo_black.png"
            alt="StateFarm"
          />
        </Box>
      ),
    },
    {
      key: 2,
      logo: (
        <Box width={{ base: '140px', tablet: '157px' }}>
          <StaticImage
            src="../assets/images/dabella_logo_black.png"
            alt="DaBella"
          />
        </Box>
      ),
    },
    {
      key: 3,
      logo: (
        <Box width={{ base: '140px', tablet: '160px' }}>
          <StaticImage
            src="../assets/images/renewal-by-andersen_logo.png"
            alt="Renewal by Andersen"
          />
        </Box>
      ),
    },
    {
      key: 4,
      logo: (
        <Box width={{ base: '140px', tablet: '189px' }}>
          <StaticImage
            src="../assets/images/travelers_vector_logo.png"
            alt="Travellers"
          />
        </Box>
      ),
    },
    {
      key: 5,
      logo: (
        <Box width={{ base: '140px', tablet: '167px' }}>
          <StaticImage
            src="../assets/images/erie_home_logo_black.png"
            alt="ErieHome"
          />
        </Box>
      ),
    },
  ];

export const MEASURE_RESOURCES: ILearningCardProps['card'][] = [
  {
    id: 1,
    description:
      'Learn how HOVER’s measurements saves Timberland Exteriors 45 minutes per job.',
    title: 'CUSTOMER TESTIMONIAL',
    linkTitle: 'Watch the video',
    linkTo: 'https://www.youtube.com/watch?v=kZT-uRv8LbA',
    titleColor: 'neutral.600',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_resources_1.jpg"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_resources_1.jpg"
      />
    ),
  },
  {
    id: 2,
    description:
      'Bees360 saves 40%-70% of claim adjustment expenses and increases claim capacity.',
    title: 'CASE STUDY',
    linkTitle: 'Read the case study',
    titleColor: 'neutral.600',
    linkTo: 'https://view.highspot.com/viewer/63fff414f8537d649b491e14',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_resources_2.jpg"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_resources_2.jpg"
      />
    ),
  },
  {
    id: 3,
    description:
      'See how HOVER uses your phone to get the job done faster, easier, and more dependably.',
    title: 'VIDEO',
    linkTitle: 'Watch the video',
    linkTo: 'https://www.youtube.com/watch?v=Aj2t3j0EwHU',
    titleColor: 'neutral.600',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt="D"
        src="../assets/images/measure_resources_3.jpg"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_resources_3.jpg"
      />
    ),
  },
  {
    id: 4,
    description:
      'Learn how to take photos with your smartphone to get your HOVER measurements.',
    title: 'VIDEO',
    linkTitle: 'Watch the video',
    linkTo:
      'https://fast.wistia.com/embed/channel/5ds5e3st4r?wchannelid=5ds5e3st4r&wmediaid=ntetqcsiqb',
    titleColor: 'neutral.600',
    image: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_resources_4.jpg"
      />
    ),
    mobileImage: (
      <StaticImage
        placeholder="blurred"
        loading="lazy"
        alt=""
        src="../assets/images/measure_resources_4.jpg"
      />
    ),
  },
];
